/**
 * ISSUE #47 FIX: Dynamic styles extracted from CourseContent.js
 * This is a factory function that creates the styles object
 * It receives the dynamic variables it needs (theme, activeColors, isMobile, themeMode)
 */

export const createStyles = ({ theme, activeColors, isMobile, themeMode }) => {
return {
  /** Full-page shell: column layout so header + main fill the viewport */
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    width: "100%",
    maxWidth: "100vw",
    backgroundColor: activeColors.background,
    color: activeColors.textDark,
    boxSizing: "border-box",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing.sm,
    width: "100%",
    minWidth: 0,
  },
  themeButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: isMobile ? "36px" : "40px",
    height: isMobile ? "36px" : "40px",
    borderRadius: theme.borderRadius.circle,
    border: "1px solid rgba(255, 255, 255, 0.35)",
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    color: activeColors.textLight,
    cursor: "pointer",
    flexShrink: 0,
    transition: theme.transitions?.normal || "0.2s ease",
  },
  /** Sidebar + scrollable main; row on desktop, column on mobile */
  mainContainer: {
    display: "flex",
    flex: 1,
    flexDirection: isMobile ? "column" : "row",
    alignItems: "stretch",
    minHeight: 0,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
  },
  sidebar: {
    flex: isMobile ? "0 0 auto" : "0 0 min(320px, 34vw)",
    width: isMobile ? "100%" : undefined,
    minWidth: 0,
    maxHeight: isMobile ? "42vh" : undefined,
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    borderRight: isMobile
      ? "none"
      : `1px solid ${activeColors.border || activeColors.lightGray}`,
    borderBottom: isMobile
      ? `1px solid ${activeColors.border || activeColors.lightGray}`
      : "none",
    backgroundColor: activeColors.surfaceElevated || activeColors.lightGray,
    boxSizing: "border-box",
  },
  contentArea: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing.md,
    padding: isMobile ? theme.spacing.sm : theme.spacing.lg,
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    backgroundColor: activeColors.background,
    boxSizing: "border-box",
  },
  /** Constrain player + lesson panel width on large screens */
  lessonMainStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing.md,
    width: "100%",
    maxWidth: "min(960px, 100%)",
    margin: "0 auto",
    boxSizing: "border-box",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "50vh",
    gap: theme.spacing.md,
    color: activeColors.gray,
  },
  spinner: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: `3px solid ${activeColors.lightGray}`,
    borderTopColor: activeColors.primary,
    boxSizing: "border-box",
  },
  modalContent: {
    backgroundColor: activeColors.surfaceElevated || "#fff",
    borderRadius: theme.borderRadius.lg,
    padding: isMobile ? theme.spacing.md : theme.spacing.lg,
    maxWidth: "420px",
    width: "100%",
    margin: theme.spacing.md,
    boxShadow: theme.shadows?.md || "0 8px 32px rgba(0,0,0,0.2)",
    boxSizing: "border-box",
  },
  modalMessage: {
    margin: `0 0 ${theme.spacing.sm}`,
    lineHeight: 1.5,
    color: activeColors.textDark,
  },
  modalDate: {
    margin: `0 0 ${theme.spacing.md}`,
    color: activeColors.gray,
    fontSize: "0.95rem",
  },
  modalButton: {
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    backgroundColor: activeColors.primary,
    color: activeColors.white,
    border: "none",
    borderRadius: theme.borderRadius.md,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.95rem",
  },
  toast: {
    position: "fixed",
    bottom: theme.spacing.lg,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 10001,
    display: "flex",
    alignItems: "center",
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    backgroundColor: activeColors.success || "#28a745",
    color: activeColors.white,
    borderRadius: theme.borderRadius.md,
    boxShadow: theme.shadows?.md || "0 4px 12px rgba(0,0,0,0.15)",
    fontSize: "0.9rem",
    maxWidth: "90vw",
  },
  appContainer: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: activeColors.background || "#ffffff",
    fontFamily:
      "'Montserrat', 'Open Sans', 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: activeColors.textDark,
    transition: "background-color 0.3s ease, color 0.3s ease",
  },
  header: {
    position: "sticky",
    top: 0,
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: isMobile
      ? `${theme.spacing.xs} ${theme.spacing.md}`
      : `${theme.spacing.sm} ${theme.spacing.lg}`,
    backgroundColor: activeColors.primary,
    color: activeColors.textLight,
    height: isMobile ? "50px" : "55px",
    boxShadow: theme.shadows.sm,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: isMobile ? theme.spacing.sm : theme.spacing.md,
  },
  // NEW: Styled back button
  backButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: isMobile ? "32px" : "36px",
    height: isMobile ? "32px" : "36px",
    borderRadius: theme.borderRadius.circle,
    border: `1px solid rgba(255, 255, 255, 0.3)`,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    color: activeColors.textLight,
    fontSize: isMobile ? "0.9rem" : "1rem",
    cursor: "pointer",
    transition: theme.transitions.normal,
    ":hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      borderColor: "rgba(255, 255, 255, 0.5)",
      transform: "translateX(-1px)",
    },
  },
  headerButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.borderRadius.md,
    border: "none",
    backgroundColor: "transparent",
    color: activeColors.textLight,
    fontWeight: 500,
    fontSize: "0.9rem",
    cursor: "pointer",
    transition: theme.transitions.normal,
    ":hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
  },
  headerTitle: {
    fontSize: isMobile ? "0.8rem" : "0.9rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    margin: 0,
    flex: 1,
    textAlign: "center",
    fontFamily: "'Montserrat', 'Open Sans', sans-serif",
  },
  // NEW: Slide toggle button for theme switching
  themeToggleButton: {
    position: "relative",
    width: isMobile ? "50px" : "60px",
    height: isMobile ? "24px" : "28px",
    backgroundColor:
      themeMode === "light"
        ? "rgba(255, 193, 7, 0.2)" // Light golden background
        : "rgba(73, 80, 87, 0.8)", // Dark gray background
    borderRadius: isMobile ? "12px" : "14px",
    border: `2px solid ${themeMode === "light" ? "#ffc107" : "#6c757d"}`,
    cursor: "pointer",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    outline: "none",
    boxShadow:
      themeMode === "light"
        ? "0 2px 8px rgba(255, 193, 7, 0.3), inset 0 2px 4px rgba(255, 193, 7, 0.1)"
        : "0 2px 8px rgba(73, 80, 87, 0.4), inset 0 2px 4px rgba(73, 80, 87, 0.2)",
    ":hover": {
      transform: "scale(1.05)",
      boxShadow:
        themeMode === "light"
          ? "0 4px 16px rgba(255, 193, 7, 0.4), inset 0 2px 4px rgba(255, 193, 7, 0.2)"
          : "0 4px 16px rgba(73, 80, 87, 0.5), inset 0 2px 4px rgba(73, 80, 87, 0.3)",
    },
    ":active": {
      transform: "scale(0.98)",
      transition: "transform 0.1s ease",
    },
  },
  // NEW: Sliding circle inside the toggle
  themeToggleSlider: {
    position: "absolute",
    top: "2px",
    left: themeMode === "light" ? "2px" : isMobile ? "24px" : "30px",
    width: isMobile ? "16px" : "20px",
    height: isMobile ? "16px" : "20px",
    backgroundColor: themeMode === "light" ? "#ffc107" : "#ffffff",
    borderRadius: "50%",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: isMobile ? "0.6rem" : "0.7rem",
    color: themeMode === "light" ? "#ffffff" : "#495057",
    boxShadow:
      themeMode === "light"
        ? "0 2px 6px rgba(255, 193, 7, 0.4), 0 0 12px rgba(255, 193, 7, 0.2)"
        : "0 2px 6px rgba(0, 0, 0, 0.3), 0 0 8px rgba(255, 255, 255, 0.1)",
    fontWeight: "bold",
  },
  mainContent: {
    flex: 1,
    padding: isMobile ? theme.spacing.md : theme.spacing.lg,
    maxWidth: "1200px",
    margin: "0 auto",
    width: "100%",
    backgroundColor: activeColors.background || "#ffffff",
    minHeight: "calc(100vh - 55px)",
    transition: "background-color 0.3s ease",
    "@media (max-width: 768px)": {
      padding: theme.spacing.sm,
      paddingBottom: "120px",
      minHeight: "calc(100vh - 50px)",
    },
  },
  card: {
    backgroundColor: activeColors.surfaceElevated || activeColors.white,
    borderRadius: isMobile ? theme.borderRadius.md : theme.borderRadius.lg,
    boxShadow: `0 2px 8px ${activeColors.shadow}`,
    border: `1px solid ${activeColors.border || activeColors.lightGray}`,
    padding: isMobile ? theme.spacing.md : theme.spacing.lg,
    marginBottom: isMobile ? theme.spacing.md : theme.spacing.lg,
    width: "100%",
    boxSizing: "border-box",
    transition:
      "background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease",
  },
  videoStylePlayerContainer: {
    position: "relative",
    borderRadius: isMobile ? theme.borderRadius.md : theme.borderRadius.lg,
    overflow: "hidden",
    backgroundColor: themeMode === "light" ? "#1a1d23" : "#0f1114",
    boxShadow: `0 4px 20px ${activeColors.shadow}`,
    border: `2px solid ${activeColors.border || activeColors.lightGray}`,
    color: activeColors.textLight,
    aspectRatio: isMobile ? "16 / 12" : "16 / 9",
    marginBottom: isMobile ? theme.spacing.md : theme.spacing.lg,
    width: "100%",
    maxWidth: "100%",
    transition: "box-shadow 0.3s ease, border-color 0.3s ease",
  },
  // CHANGED: Updated player visual area to support a background image with top overlay for audio info
  playerVisualArea: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    position: "relative",
    zIndex: 1,
    // The background image is set here. Make sure 'audio-background.jpg' exists in your /public folder.
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('/audiobg.jpg')`,
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  },
  // NEW: Top overlay for audio info
  audioInfoOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: theme.spacing.md,
    background:
      "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, transparent 100%)",
    zIndex: 2,
  },
  // NEW: Audio name in top left
  audioNameDisplay: {
    color: activeColors.textLight,
    fontSize: isMobile ? "0.9rem" : "1.1rem",
    fontWeight: 600,
    textShadow: "0 1px 3px rgba(0,0,0,0.8)",
    maxWidth: "70%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: "'Montserrat', 'Open Sans', sans-serif",
  },
  // NEW: Small audio icon in top right
  audioIconTopRight: {
    color: activeColors.primary,
    fontSize: isMobile ? "1.2rem" : "1.5rem",
    opacity: 0.9,
    textShadow: "0 1px 3px rgba(0,0,0,0.8)",
  },
  // FIND THIS STYLE BLOCK IN YOUR CODE:
  playerControlsOverlay: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    // ADD THIS LINE RIGHT HERE:
    zIndex: 2,
    padding: isMobile
      ? `${theme.spacing.xs} ${theme.spacing.sm}`
      : `${theme.spacing.sm} ${theme.spacing.md}`,
    background:
      themeMode === "light"
        ? "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 60%, transparent 100%)"
        : "linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.8) 60%, transparent 100%)",
    display: "flex",
    flexDirection: "column",
    gap: isMobile ? theme.spacing.xs : theme.spacing.sm,
    transition: "background 0.3s ease",
  },
  progressContainer: {
    display: "flex",
    alignItems: "center",
    gap: isMobile ? theme.spacing.xs : theme.spacing.sm,
    width: "100%",
  },
  progressBar: {
    flex: 1,
    height: isMobile ? "4px" : "6px",
    backgroundColor: "rgba(255,255,255,0.3)",
    borderRadius: theme.borderRadius.lg,
    position: "relative",
    cursor: "pointer",
  },
  progressFill: {
    height: "100%",
    backgroundColor: activeColors.primary,
    borderRadius: theme.borderRadius.lg,
    position: "relative",
  },
  progressThumb: {
    position: "absolute",
    top: "50%",
    right: "-6px",
    transform: "translateY(-50%)",
    width: isMobile ? "8px" : "12px",
    height: isMobile ? "8px" : "12px",
    backgroundColor: activeColors.primary,
    borderRadius: theme.borderRadius.circle,
    boxShadow: theme.shadows.sm,
  },
  timeDisplay: {
    fontSize: isMobile ? "0.65rem" : "0.75rem",
    color: activeColors.textLight,
    minWidth: isMobile ? "30px" : "40px",
    textAlign: "center",
  },
  controlsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing.sm,
  },
  // CHANGED: Controls are now more compact on small screens
  controlsGroup: {
    display: "flex",
    alignItems: "center",
    gap: isMobile ? theme.spacing.xs : theme.spacing.sm,
  },
  // CHANGED: Smaller control buttons on phones
  controlButton: {
    width: isMobile ? "28px" : "36px",
    height: isMobile ? "28px" : "36px",
    borderRadius: theme.borderRadius.circle,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    border: "none",
    color: activeColors.textLight,
    cursor: "pointer",
    transition: "all 0.3s ease",
    fontSize: isMobile ? "0.8rem" : "1rem",
    ":hover": {
      backgroundColor:
        themeMode === "light"
          ? "rgba(255,255,255,0.15)"
          : "rgba(255,255,255,0.2)",
      transform: "scale(1.1)",
    },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  // CHANGED: Smaller play button on phones
  playButton: {
    width: isMobile ? "32px" : "42px",
    height: isMobile ? "32px" : "42px",
    backgroundColor: activeColors.primary,
    color: activeColors.textLight,
    fontSize: isMobile ? "0.9rem" : "1.1rem",
    ":hover": { backgroundColor: activeColors.primaryDark },
  },
  volumeControl: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  // CHANGED: Smaller volume bar on phones to ensure it's visible
  volumeBar: {
    width: isMobile ? "40px" : "80px",
    height: "4px",
    backgroundColor: "rgba(255,255,255,0.3)",
    borderRadius: theme.borderRadius.lg,
    position: "relative",
    cursor: "pointer",
  },
  volumeFill: {
    height: "100%",
    backgroundColor: activeColors.primary,
    borderRadius: theme.borderRadius.lg,
  },
  volumeThumb: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: isMobile ? "8px" : "10px",
    height: isMobile ? "8px" : "10px",
    backgroundColor: activeColors.primary,
    borderRadius: theme.borderRadius.circle,
  },
  playbackRate: { position: "relative" },
  // CHANGED: Smaller playback rate button on phones
  rateButton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xs,
    backgroundColor: "transparent",
    border: `1px solid ${activeColors.textLight}`,
    color: activeColors.textLight,
    borderRadius: theme.borderRadius.md,
    padding: isMobile
      ? `${theme.spacing.xs} ${theme.spacing.xs}`
      : `${theme.spacing.xs} ${theme.spacing.sm}`,
    fontSize: isMobile ? "0.7rem" : "0.8rem",
    cursor: "pointer",
    transition: theme.transitions.normal,
    ":hover": { backgroundColor: "rgba(255,255,255,0.1)" },
  },
  rateOptions: {
    position: "absolute",
    bottom: "120%",
    right: 0,
    backgroundColor: activeColors.dark,
    border: `1px solid ${activeColors.lightGray}`,
    borderRadius: theme.borderRadius.md,
    boxShadow: theme.shadows.md,
    padding: theme.spacing.sm,
    minWidth: "100px",
    zIndex: 10,
  },
  rateOption: {
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.borderRadius.sm,
    fontSize: "0.8rem",
    cursor: "pointer",
    transition: theme.transitions.fast,
    color: activeColors.textLight,
    ":hover": { backgroundColor: activeColors.secondary },
  },
  activeRate: {
    backgroundColor: activeColors.primary,
    color: activeColors.white,
    fontWeight: 600,
  },
  lessonTitle: {
    fontSize: "1.75rem",
    fontWeight: 700,
    margin: `0 0 ${theme.spacing.sm} 0`,
    color: activeColors.textDark,
    fontFamily: "'Montserrat', 'Nunito', sans-serif",
    letterSpacing: "-0.02em",
  },
  sectionTitle: {
    fontSize: "1.25rem",
    fontWeight: 600,
    margin: `0 0 ${theme.spacing.md} 0`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    color: activeColors.textDark,
    fontFamily: "'Montserrat', 'Open Sans', sans-serif",
  },
  audioList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.sm,
  },
  audioItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    backgroundColor: activeColors.white,
    border: `1px solid ${activeColors.lightGray}`,
    cursor: "pointer",
    transition: theme.transitions.normal,
    ":hover": {
      borderColor: activeColors.primary,
      backgroundColor: activeColors.lightGray,
    },
  },
  activeAudioItem: {
    backgroundColor: activeColors.primaryLight,
    borderColor: activeColors.primary,
    color: activeColors.white,
  },
  audioItemInfo: { flex: 1, minWidth: 0, marginRight: theme.spacing.md },
  audioItemTitle: {
    fontWeight: 500,
    fontSize: "0.95rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: theme.spacing.xs,
    fontFamily: "'Open Sans', 'Nunito', sans-serif",
  },
  audioItemMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    fontSize: "0.75rem",
    color: activeColors.gray,
  },
  pdfGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: theme.spacing.md,
  },
  pdfCard: {
    display: "flex",
    alignItems: "center",
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${activeColors.lightGray}`,
    textDecoration: "none",
    color: activeColors.textDark,
    transition: theme.transitions.normal,
    ":hover": {
      boxShadow: theme.shadows.md,
      borderColor: activeColors.primary,
    },
  },
  pdfIcon: {
    padding: theme.spacing.lg,
    backgroundColor: activeColors.lightGray,
    color: activeColors.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.5rem",
  },
  pdfDetails: { padding: theme.spacing.md, flex: 1, minWidth: 0 },
  pdfTitle: {
    fontWeight: 500,
    fontSize: "0.9rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: theme.spacing.xs,
  },
  lessonActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing.lg,
    paddingTop: theme.spacing.md,
    borderTop: `1px solid ${activeColors.lightGray}`,
  },
  badge: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.borderRadius.md,
    fontWeight: 500,
    fontSize: "0.9rem",
  },
  successBadge: {
    backgroundColor: activeColors.success,
    color: activeColors.white,
  },
  button: {
    padding: isMobile
      ? `${theme.spacing.sm} ${theme.spacing.md}`
      : `${theme.spacing.md} ${theme.spacing.lg}`,
    borderRadius: theme.borderRadius.md,
    border: "none",
    fontWeight: 600,
    fontSize: isMobile ? "0.85rem" : "0.9rem",
    cursor: "pointer",
    transition: theme.transitions.normal,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing.sm,
    fontFamily: "'Montserrat', 'Nunito', sans-serif",
    minHeight: isMobile ? "44px" : "auto", // Better touch targets on mobile
    width: isMobile ? "100%" : "auto", // Full width on mobile
  },
  primaryButton: {
    backgroundColor: activeColors.primary,
    color: activeColors.white,
    ":hover": { backgroundColor: activeColors.primaryDark },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing.xl,
    color: activeColors.gray,
  },
  emptyIcon: {
    width: "60px",
    height: "60px",
    borderRadius: theme.borderRadius.circle,
    backgroundColor: activeColors.lightGray,
    color: activeColors.primary,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.75rem",
    marginBottom: theme.spacing.lg,
  },
  emptyTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: activeColors.textDark,
    marginBottom: theme.spacing.sm,
    fontFamily: "'Montserrat', 'Open Sans', sans-serif",
  },
  emptyText: {
    fontSize: "0.95rem",
    maxWidth: "400px",
    lineHeight: 1.5,
    marginBottom: theme.spacing.lg,
    fontFamily: "'Open Sans', 'Nunito', sans-serif",
  },
  chapterItem: {
    padding: isMobile ? theme.spacing.sm : theme.spacing.md,
    borderBottom: `1px solid ${
      activeColors.border || activeColors.lightGray
    }`,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: isMobile ? "50px" : "auto",
    backgroundColor: activeColors.surfaceElevated || activeColors.white,
    ":hover": {
      backgroundColor: activeColors.surface || activeColors.lightGray,
      transform: "translateX(2px)",
    },
  },
  activeChapter: {
    backgroundColor: activeColors.primaryLight,
    color: activeColors.white,
  },
  chapterTitle: {
    fontWeight: 600,
    fontSize: isMobile ? "0.9rem" : "1rem",
    margin: 0,
    color: activeColors.textDark,
    fontFamily: "'Montserrat', 'Open Sans', sans-serif",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  lessonItem: {
    padding: isMobile
      ? `${theme.spacing.sm} ${theme.spacing.md}`
      : `${theme.spacing.md} ${theme.spacing.lg}`,
    paddingLeft: isMobile ? theme.spacing.lg : theme.spacing.xl,
    borderBottom: `1px solid ${
      activeColors.border || activeColors.lightGray
    }`,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    minHeight: isMobile ? "48px" : "auto",
    ":hover": {
      backgroundColor: activeColors.surface || activeColors.lightGray,
      transform: "translateX(2px)",
    },
    background: activeColors.surfaceElevated || activeColors.white,
  },
  activeLesson: {
    backgroundColor: activeColors.primaryLight,
    color: activeColors.white,
    borderLeft: `4px solid ${activeColors.primary}`,
  },
  lockedLesson: {
    opacity: 0.7,
    cursor: "not-allowed",
    backgroundColor: activeColors.lightGray,
    ":hover": { backgroundColor: activeColors.lightGray },
  },
  lessonIcon: {
    fontSize: "1.1rem",
    marginRight: theme.spacing.md,
    color: activeColors.gray,
  },
  activeIcon: { color: activeColors.white },
  completedIcon: { color: activeColors.success },
  lockedIcon: { color: activeColors.danger },
  lessonDetails: { flex: 1, minWidth: 0 },
  lessonName: {
    fontWeight: 500,
    fontSize: isMobile ? "0.85rem" : "0.95rem",
    marginBottom: theme.spacing.xs,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontFamily: "'Open Sans', 'Nunito', sans-serif",
    lineHeight: isMobile ? "1.4" : "1.5",
  },
  lessonMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    fontSize: "0.75rem",
    color: activeColors.gray,
  },
  lessonStatus: { marginLeft: "auto", fontSize: "1.1rem" },
  // NEW: Modern exam container styles
  examContainer: {
    backgroundColor: activeColors.white,
    borderRadius: theme.borderRadius.lg,
    overflow: "hidden",
    marginBottom: theme.spacing.lg,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${activeColors.lightGray}`,
  },
  examHeader: {
    background:
      themeMode === "light"
        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        : "linear-gradient(135deg, #434343 0%, #000000 100%)",
    color: activeColors.white,
    padding: isMobile ? theme.spacing.lg : theme.spacing.xl,
    textAlign: "center",
    position: "relative",
    "::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(10px)",
    },
  },
  examTitle: {
    fontSize: isMobile ? "1.4rem" : "1.8rem",
    fontWeight: 700,
    margin: 0,
    marginBottom: theme.spacing.sm,
    fontFamily: "'Montserrat', 'Open Sans', sans-serif",
    textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
  },
  examSubtitle: {
    fontSize: isMobile ? "0.9rem" : "1rem",
    opacity: 0.9,
    margin: 0,
    fontFamily: "'Open Sans', 'Nunito', sans-serif",
  },
  examStatsGrid: {
    display: "grid",
    gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
    gap: theme.spacing.md,
    padding: isMobile ? theme.spacing.md : theme.spacing.lg,
    backgroundColor: activeColors.lightGray,
  },
  examStatCard: {
    backgroundColor: activeColors.white,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    textAlign: "center",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${activeColors.lightGray}`,
  },
  examStatValue: {
    fontSize: isMobile ? "1.4rem" : "1.6rem",
    fontWeight: 700,
    color: activeColors.primary,
    marginBottom: theme.spacing.xs,
    fontFamily: "'Montserrat', sans-serif",
  },
  examStatLabel: {
    fontSize: isMobile ? "0.75rem" : "0.85rem",
    color: activeColors.gray,
    fontWeight: 500,
    fontFamily: "'Open Sans', sans-serif",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  // NEW: Exam result styles
  examResultContainer: {
    backgroundColor: activeColors.white,
    borderRadius: theme.borderRadius.lg,
    overflow: "hidden",
    marginBottom: theme.spacing.lg,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  },
  examResultHeader: {
    background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
    color: activeColors.white,
    padding: isMobile ? theme.spacing.md : theme.spacing.lg,
    textAlign: "center",
  },
  examResultTitle: {
    fontSize: isMobile ? "1.2rem" : "1.4rem",
    fontWeight: 600,
    margin: 0,
    fontFamily: "'Montserrat', sans-serif",
  },
  examResultBody: {
    padding: isMobile ? theme.spacing.md : theme.spacing.lg,
  },
  examResultGrid: {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  examResultCard: {
    backgroundColor: activeColors.lightGray,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    textAlign: "center",
    border: `2px solid ${activeColors.primary}`,
  },
  examResultValue: {
    fontSize: isMobile ? "1.5rem" : "1.8rem",
    fontWeight: 700,
    color: activeColors.primary,
    marginBottom: theme.spacing.xs,
    fontFamily: "'Montserrat', sans-serif",
  },
  examResultLabel: {
    fontSize: "0.8rem",
    color: activeColors.gray,
    fontWeight: 500,
    fontFamily: "'Open Sans', sans-serif",
    textTransform: "uppercase",
  },
  // NEW: Action buttons container
  examActionsContainer: {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    gap: theme.spacing.sm,
    justifyContent: "center",
    alignItems: "center",
    paddingTop: theme.spacing.lg,
    borderTop: `1px solid ${activeColors.lightGray}`,
  },
  // NEW: Secondary button for viewing answers
  secondaryButton: {
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    borderRadius: theme.borderRadius.md,
    border: `2px solid ${activeColors.primary}`,
    backgroundColor: "transparent",
    color: activeColors.primary,
    fontWeight: 600,
    fontSize: "0.9rem",
    cursor: "pointer",
    transition: theme.transitions.normal,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing.sm,
    fontFamily: "'Montserrat', 'Nunito', sans-serif",
    width: isMobile ? "100%" : "auto",
    ":hover": {
      backgroundColor: activeColors.primary,
      color: activeColors.white,
    },
  },
  // NEW: Question styles for simple, mobile-friendly design
  questionItem: {
    marginBottom: isMobile ? theme.spacing.md : theme.spacing.lg,
    paddingBottom: 0,
    borderBottom: "none",
    backgroundColor: activeColors.white,
    borderRadius: "8px",
    padding: isMobile ? theme.spacing.md : theme.spacing.lg,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${activeColors.lightGray}`,
  },
  questionHeader: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: isMobile ? theme.spacing.sm : theme.spacing.md,
    gap: isMobile ? theme.spacing.sm : theme.spacing.md,
  },
  questionNumber: {
    backgroundColor: activeColors.primary,
    color: activeColors.white,
    width: isMobile ? "28px" : "32px",
    height: isMobile ? "28px" : "32px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: isMobile ? "0.9rem" : "1rem",
    fontWeight: 600,
    fontFamily: "'Montserrat', sans-serif",
  },
  questionText: {
    margin: 0,
    flex: 1,
    fontSize: isMobile ? "0.95rem" : "1.1rem",
    lineHeight: 1.5,
    fontFamily: "'Open Sans', 'Nunito', sans-serif",
    color: activeColors.textDark,
    fontWeight: 500,
  },
  optionsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: isMobile ? theme.spacing.sm : theme.spacing.md,
    marginTop: isMobile ? theme.spacing.sm : theme.spacing.md,
  },
  optionItem: {
    padding: isMobile ? theme.spacing.sm : theme.spacing.md,
    borderRadius: "6px",
    border: `2px solid ${activeColors.lightGray}`,
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: isMobile ? theme.spacing.sm : theme.spacing.md,
    backgroundColor: activeColors.white,
  },
  selectedOption: {
    backgroundColor: activeColors.primary,
    borderColor: activeColors.primary,
    color: activeColors.white,
  },
  optionMarker: {
    width: isMobile ? "24px" : "28px",
    height: isMobile ? "24px" : "28px",
    borderRadius: "4px",
    backgroundColor: activeColors.lightGray,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: isMobile ? "0.8rem" : "0.9rem",
    fontWeight: 600,
    color: activeColors.textDark,
    flexShrink: 0,
    fontFamily: "'Montserrat', sans-serif",
  },
  selectedMarker: {
    backgroundColor: activeColors.white,
    color: activeColors.primary,
  },
  optionText: {
    flex: 1,
    fontSize: isMobile ? "0.9rem" : "1rem",
    fontFamily: "'Open Sans', 'Nunito', sans-serif",
    lineHeight: 1.4,
    fontWeight: 400,
  },
  questionMeta: {
    fontSize: "0.75rem",
    color: activeColors.gray,
    marginTop: theme.spacing.sm,
    marginLeft: "38px",
  },

  // --- EXAM TIMER STYLES ---
  timerContainer: {
    position: "sticky",
    top: isMobile ? "50px" : "55px",
    zIndex: 999,
    backgroundColor: activeColors.white,
    borderRadius: theme.borderRadius.md,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
    margin: `0 0 ${theme.spacing.lg} 0`,
    overflow: "hidden",
    border: `2px solid ${activeColors.lightGray}`,
  },
  timerHeader: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: activeColors.white,
    padding: isMobile ? theme.spacing.md : theme.spacing.lg,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing.sm,
  },
  timerIcon: {
    fontSize: isMobile ? "1.2rem" : "1.5rem",
  },
  timerTitle: {
    fontSize: isMobile ? "1rem" : "1.1rem",
    fontWeight: 600,
    margin: 0,
    fontFamily: "'Montserrat', sans-serif",
  },
  timerDisplay: {
    padding: isMobile ? theme.spacing.md : theme.spacing.lg,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing.sm,
    backgroundColor: activeColors.lightGray,
  },
  timerTime: {
    fontSize: isMobile ? "2rem" : "2.5rem",
    fontWeight: 700,
    fontFamily: "'Montserrat', sans-serif",
    textAlign: "center",
  },
  timerProgress: {
    width: "100%",
    height: isMobile ? "8px" : "10px",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    borderRadius: theme.borderRadius.lg,
    overflow: "hidden",
    marginTop: theme.spacing.sm,
  },
  timerProgressFill: {
    height: "100%",
    borderRadius: theme.borderRadius.lg,
    transition: "width 1s linear, background-color 0.3s ease",
  },
  timerWarning: {
    fontSize: isMobile ? "0.8rem" : "0.9rem",
    fontWeight: 500,
    textAlign: "center",
    marginTop: theme.spacing.sm,
    fontFamily: "'Open Sans', sans-serif",
  },

  // --- EXAM MODAL STYLES ---
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor:
      themeMode === "light" ? "rgba(0, 0, 0, 0.8)" : "rgba(0, 0, 0, 0.9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: isMobile ? "0" : theme.spacing.sm,
    transition: "background-color 0.3s ease",
  },
  modalContainer: {
    backgroundColor: activeColors.surfaceElevated || "#ffffff",
    borderRadius: isMobile ? "0" : "12px",
    boxShadow: `0 8px 32px ${activeColors.shadow}`,
    border: isMobile
      ? "none"
      : `2px solid ${activeColors.border || activeColors.primary}`,
    width: "100%",
    maxWidth: isMobile ? "100vw" : "800px",
    maxHeight: isMobile ? "100vh" : "90vh",
    height: isMobile ? "100vh" : "auto",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    margin: 0,
    transition: "background-color 0.3s ease, border-color 0.3s ease",
  },
  modalHeader: {
    backgroundColor: activeColors.primary,
    color: activeColors.white,
    padding: isMobile ? theme.spacing.md : theme.spacing.lg,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  modalTitle: {
    fontSize: isMobile ? "1.1rem" : "1.4rem",
    fontWeight: 600,
    margin: 0,
    fontFamily: "'Montserrat', sans-serif",
  },
  modalCloseButton: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    border: "none",
    borderRadius: "8px",
    width: "36px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: activeColors.white,
    cursor: "pointer",
    fontSize: "1rem",
    transition: "all 0.2s ease",
  },
  modalBody: {
    flex: 1,
    overflow: "auto",
    padding: 0,
    backgroundColor: "#ffffff",
    WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
  },
  modalTimerSection: {
    position: "sticky",
    top: 0,
    backgroundColor: activeColors.white,
    padding: isMobile ? theme.spacing.md : theme.spacing.lg,
    borderBottom: `2px solid ${activeColors.lightGray}`,
    marginBottom: 0,
    zIndex: 5,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  },
  compactTimer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: isMobile ? theme.spacing.md : theme.spacing.lg,
    backgroundColor: activeColors.primary,
    borderRadius: "8px",
    color: activeColors.white,
  },
  compactTimerLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  compactTimerIcon: {
    fontSize: isMobile ? "1.2rem" : "1.4rem",
    color: activeColors.white,
  },
  compactTimerText: {
    fontSize: isMobile ? "0.9rem" : "1rem",
    fontWeight: 600,
    color: activeColors.white,
    fontFamily: "'Montserrat', sans-serif",
  },
  compactTimerTime: {
    fontSize: isMobile ? "1.1rem" : "1.3rem",
    fontWeight: 700,
    fontFamily: "'Montserrat', sans-serif",
    color: activeColors.white,
  },
  examStartContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? theme.spacing.md : theme.spacing.xl,
    textAlign: "center",
    gap: isMobile ? theme.spacing.sm : theme.spacing.lg,
    backgroundColor: "#ffffff",
    borderRadius: isMobile ? "4px" : "8px",
    border: `2px solid ${activeColors.primary}`,
    margin: isMobile ? theme.spacing.sm : 0,
    minHeight: isMobile ? "auto" : "400px",
  },
  examStartIcon: {
    width: isMobile ? "60px" : "80px",
    height: isMobile ? "60px" : "80px",
    borderRadius: "8px",
    backgroundColor: activeColors.primary,
    color: activeColors.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: isMobile ? "1.5rem" : "2rem",
    marginBottom: theme.spacing.sm,
  },
  examStartTitle: {
    fontSize: isMobile ? "1.2rem" : "1.5rem",
    fontWeight: 600,
    color: activeColors.textDark,
    marginBottom: theme.spacing.sm,
    fontFamily: "'Montserrat', sans-serif",
  },
  examStartDescription: {
    fontSize: isMobile ? "0.9rem" : "1rem",
    color: activeColors.gray,
    lineHeight: 1.5,
    maxWidth: "400px",
    marginBottom: theme.spacing.lg,
    fontFamily: "'Open Sans', sans-serif",
  },
  examStartButton: {
    backgroundColor: activeColors.primary,
    color: activeColors.white,
    border: "none",
    borderRadius: "8px",
    padding: isMobile
      ? `${theme.spacing.md} ${theme.spacing.lg}`
      : `${theme.spacing.lg} ${theme.spacing.xl}`,
    fontSize: isMobile ? "0.9rem" : "1rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    fontFamily: "'Poppins', sans-serif",
  },

  tabsContainer: {
    display: "flex",
    borderBottom: `1px solid ${activeColors.lightGray}`,
    backgroundColor: activeColors.white,
    color: activeColors.textDark,
    marginBottom: theme.spacing.lg,
  },
  tabButton: {
    padding: `${theme.spacing.md} ${theme.spacing.lg}`,
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    fontSize: "1rem",
    fontWeight: 600,
    color: activeColors.gray,
    borderBottom: "3px solid transparent",
    transition: theme.transitions.normal,
    fontFamily: "'Montserrat', 'Open Sans', sans-serif",
  },
  activeTabButton: {
    color: activeColors.primary,
    borderBottom: `3px solid ${activeColors.primary}`,
  },
  tabContentContainer: {
    backgroundColor: activeColors.white,
    borderRadius: theme.borderRadius.md,
    color: activeColors.textDark,
    padding: isMobile ? theme.spacing.sm : theme.spacing.md,
  },
};
};
